import {
  Component,
  Element,
  Event,
  type EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import type { Facet, FacetValue, Filter } from '../../../api/SearchApi';
import type { FacetChange } from './FacetChange';
import {
  type FacetCose,
  type FacetValueCose,
  FacetTypeEnum,
  type FacetType,
} from '../../../api/model/response/facets/Facets';
import type { FacetData } from '../../../models/common/facet-data.model';
import {
  FacetsService,
  type FilterRequestUnique,
} from '../../../utils/services/facets/facets.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { mergeDeepFilters } from '../../../utils/helpers/merge-deep';
import type { FilterRequest } from '../../../models/cose-api/request/query-body-request.model';
import { addHorizontalClass } from './utils';
import type { SearchableFacetInputChanged } from './subcomponents/SearchableFacetInputChanged';
import { SearchableFacet } from './subcomponents/searchable-facet';
import {
  FILTER_PRICE_KEY,
  FILTERS_ID_SEPARATOR,
  FILTER_LOCALE_KEY,
} from '../../../constants/filterConstants';
import type { labelProviderInterface } from '../../LabelProviderInterface';
import {FacetTag} from './subcomponents/facet-tag/facet-tag';
import {FACET_TAG} from './subcomponents/facet-tag/facet-tags';

@Component({
  tag: 'uiaas-search-facet',
  styleUrl: 'uiaas-search-facet.scss',
  shadow: false,
})
export class SearchFacet {
  @Prop() readonly facetTitle: string;
  @Prop() readonly facetTranslation?: string;
  @Prop() readonly values: Array<FacetValue | FacetValueCose>;
  @Prop({ mutable: true }) labelProvider?: labelProviderInterface;
  @Prop({ mutable: true }) commonFilterTranslations?: labelProviderInterface;

  @Prop() readonly facetStateCose? = {};
  @Prop() readonly facetData: Facet | FacetData | FacetCose;
  @Prop({ attribute: 'tab-id' }) readonly tabId: string;
  @Prop() readonly mobileView;
  @Prop() readonly itemsLimit;
  @Event() toggle: EventEmitter;
  @Prop() readonly defaultOpened: boolean = false;
  @State() toggled: boolean = this.defaultOpened;
  @Prop() readonly hideFacetTitle: boolean = false;
  @Prop() readonly reflectedState;
  @Prop() readonly disableFiltersOnLoading: boolean;
  @Prop() readonly rtl: boolean;

  @Prop() readonly scrollToTop: boolean = true;

  @Element() el;

  @Prop() readonly horizontalFilterPanel: boolean = false;
  @Prop() readonly index: number;
  @Prop() readonly currentFacetOpened?: number;
  @Prop() readonly enforcedFilters: Array<Filter> = [];
  @Prop() readonly defaultQuartzColor?: string;

  @State() facetState = {};
  /**
   * Prop used for clearing facets on searchable facet input change
   */
  @Prop() readonly searchFacetsValuesToRemove: Array<string>;
  @Watch('searchFacetsValuesToRemove')
  clearValue(): void {
      if (this.searchFacetsValuesToRemove.indexOf(this.facetId) !== -1) {
        this.searchFacetValue = '';
      }
  }

  @State() hideFilterItems = true;
  @Event({
    eventName: 'search:facetChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    facetChanged: EventEmitter<FacetChange>;

  @Event({
    eventName: 'uiaas:scrollToTop',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    scrollTopEvent;

  private facetService!: FacetsService;
  private facets!: any[]; //eslint-disable-line @typescript-eslint/no-explicit-any
  private facetsState!: any[]; //eslint-disable-line @typescript-eslint/no-explicit-any
  private unsubscriber$: Subject<any> = new Subject<any>(); //eslint-disable-line @typescript-eslint/no-explicit-any

  @Event({
    eventName: 'search:facetToggled',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    facetToggled: EventEmitter<number>;

  get facetId(): string {
      return this.facetData['id'];
  }

  private handleClick() {
      this.hideFilterItems = !this.hideFilterItems;
  }

  private toggleComponent() {
      this.toggled = !this.toggled;
      this.toggle.emit({ visible: this.toggled });
  }

  @Watch('currentFacetOpened')
  handlecurrentFacetOpened() {
      if (this.horizontalFilterPanel && this.currentFacetOpened !== undefined) {
        if (this.currentFacetOpened !== this.index && this.toggled) {
          this.toggled = false;
          this.toggle.emit({ visible: false });
        }
      }
  }

  @Listen('search:searchableFacetInputChanged')
  handleSearchFacetChanged(
    event: CustomEvent<SearchableFacetInputChanged>,
  ): void {
      if (event.detail.uniqueId == this.facetId) {
        this.updateSearchFacet(event.detail.value);
      }
  }

  @Listen('search:facetRangeChanged')
  handleRangeFacetChanged(e): void {
      e.preventDefault();
      e.stopPropagation();

      const additionalData = {
        toValue: e.detail['rangeToValue'],
        priceKey: e.detail['priceKey'],
      };
      this.onFacetChange(e.detail, true, false, additionalData, false);

      this.toggled = true;

      this.facetChanged.emit({
        facetValue: e.detail.value,
        facetType: this.facetTitle,
        facetName: e?.detail?.value,
        facetTranslation: null,
        facetTypeTranslation: this.facetTranslation,
        facetCount: null,
        facetQuery: null,
        value: e?.detail?.value,
        action: 'add',
        id: e.detail?.id,
        uniqueId: e.detail?.priceKey,
      });
  }

  private handleOpenView() {
      if (this.hideFacetTitle) {
        return '--open';
      } else {
        if (this.toggled) {
          return '--open';
        }
        return '--close';
      }
  }

  @Watch('mobileView')
  updateView() {
      if (this.mobileView) {
        this.toggled = false;
      } else {
        if (this.horizontalFilterPanel) {
          if (Object.keys(this.facetState).length > 0) {
            this.toggled = true;
          }
        } else {
          this.toggled = this.defaultOpened;
        }
      }
  }

  @Event({
    eventName: 'search:searchableFacetInputChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    inputChangedEvent: EventEmitter<SearchableFacetInputChanged>;

  @Event({
    eventName: 'search:searchableFacetInputEnterClicked',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    inputEnterClicked: EventEmitter<SearchableFacetInputChanged>;

  @Prop({ mutable: true }) searchFacetValue: string;

  private updateFacetState() {
      let isCoseFacetOpen = false;
      if (Object.keys(this.facetStateCose).length > 0) {
        const adaptCoseState = Object.keys(this.facetStateCose).map((el) =>
          el.split('.').join(FILTERS_ID_SEPARATOR),
        );
        isCoseFacetOpen = this.facetData.fieldValues?.some((el) =>
          adaptCoseState.includes(el.uniqueId),
        );
      }
      if (this.horizontalFilterPanel) {
        if (Object.keys(this.facetState).length > 0 && this.toggled) {
          this.toggled = true;
        }
      } else {
        if (Object.keys(this.facetState).length > 0 || isCoseFacetOpen) {
          this.toggled = true;
        } else {
          this.toggled =
          this.defaultOpened ||
          (this.facetData as FacetCose).children.some(
            (filter) => filter.selected,
          );
        }
      }

      /*
        Open load more filters when some is selected
       */
      if ((this.facetData as FacetCose)?.children) {
        let lastSelectedFilterIndex;
        (this.facetData as FacetCose).children.forEach((filter, i) => {
            if (filter.selected) {
              lastSelectedFilterIndex = i;
            }
        });

        if (lastSelectedFilterIndex) {
          const limit = parseInt(this.itemsLimit);
          if (limit < lastSelectedFilterIndex + 1) {
            this.hideFilterItems = false;
          }
        }
      }
  }

  componentWillLoad() {
      this.facetService = FacetsService.getInstance(this.tabId);
      this.facetService.facets
        .pipe(takeUntil(this.unsubscriber$))
        .subscribe((facets) => {
            this.facets = facets;
        });

      this.facetService.facetsState
        .pipe(takeUntil(this.unsubscriber$))
        .subscribe((state) => {
            this.facetsState = state;
        });

      this.facetService.onClearFacet
        .pipe(
          takeUntil(this.unsubscriber$),
          filter((value: FacetValue) => {
              const chipsState = this.facetService.facetChipsState$.value;
              const index = chipsState.findIndex(
                (chips) => chips.uniqueId === value.uniqueId,
              );
              return !!value && value.uniqueId && index !== -1;
          }),
        )
        .subscribe((filterValue) => {
            this.onFacetChange(filterValue, false, false, null, false);
            this.facetChanged.emit({
              facetValue: filterValue.fieldValue,
              facetType: this.facetTitle,
              facetName: filterValue.fieldValue,
              facetTranslation: filterValue.fieldTranslation,
              facetTypeTranslation: this.facetTranslation,
              facetCount: filterValue.count,
              facetQuery: filterValue.query,
              value: filterValue.fieldValue,
              action: 'remove',
              id: filterValue.uniqueId
                ? filterValue.uniqueId.split(FILTERS_ID_SEPARATOR)[0]
                : undefined,
              uniqueId: filterValue.uniqueId,
            });
        });

      this.updateView();
      this.updateFacetState();
      if (this.horizontalFilterPanel) {
        if (this.enforcedFilters.length > 0) {
          const facet = this.enforcedFilters.find((x) => x.name == this.facetId);
          if (facet) {
            this.searchFacetValue = facet.value;
          }
        }
      }
      if (this.horizontalFilterPanel) {
        this.toggleComponent();
      }
      if (!this.labelProvider) {
        this.labelProvider = (fallback) => {
            return fallback;
        };
      }
      if (!this.commonFilterTranslations) {
        this.commonFilterTranslations = (name, fallback) => {
            return fallback;
        };
      }
  }

  componentDidLoad() {
      this.applyRtlForCheckboxes();
  }

  componentDidUpdate() {
      this.applyRtlForCheckboxes();
  }

  private async onFacetChange(
    facetValue: FacetValue,
    selected: boolean,
    searchFacet = false,
    additionalData?,
    singleOptionFacet = false,
  ): Promise<void> {
      const tree: FilterRequest[] | FilterRequestUnique[] = this.facetsState;
      if (this.facets && selected) {
        this.facets = FacetsService.setUnique(this.facets);
        let branch: FilterRequest | FilterRequestUnique | any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any

        this.facets.forEach((facet) => {
            const root = [
              { uniqueId: facet.uniqueId, valueId: this.facetId || facet.id },
            ];

            const filters: Array<{ uniqueId: string; valueId: string }> =
          FacetsService.findFacet(
            facet.children,
            facetValue['uniqueId'],
            facet.uniqueId,
            facet.id,
            root,
            additionalData,
          );
            const facetRoot = {
              id: filters[0].valueId || this.facetId || facet.id,
              uniqueId: filters[0].uniqueId,
              children: [],
            };

            const valuesFilters: Array<{ uniqueId: string; valueId: string }> =
          filters.slice(this.facetId ? 2 : 1, filters.length);

            // adjustment so that if uniqueId consists of three elements separated by ":", the array is built correctly
            if (
              valuesFilters[0]?.uniqueId &&
          valuesFilters[0]?.uniqueId?.split(FILTERS_ID_SEPARATOR).length === 3
            ) {
              const splittedUniqueId =
            valuesFilters[0].uniqueId.split(FILTERS_ID_SEPARATOR);
              const parentFilterValue = {
                uniqueId:
              splittedUniqueId[0] + FILTERS_ID_SEPARATOR + splittedUniqueId[1],
                valueId: splittedUniqueId[1],
              };
              valuesFilters.unshift(parentFilterValue);
              facetRoot.id = facet.id;
            }

            if (searchFacet) {
              valuesFilters.push({
                uniqueId:
              this.facetId + FILTERS_ID_SEPARATOR + facetValue.fieldValue,
                valueId: facetValue.fieldValue,
              });
            }
            if (facetRoot.id === facet.id && filters.length > 1) {
              const valueTo = FacetsService.arrayToTree(
                valuesFilters,
                facetRoot,
                additionalData,
              );
              branch = mergeDeepFilters(branch, valueTo);
            }
        });

        if (tree.some((v) => v.id === branch['id'])) {
          const index = tree.findIndex((v) => v.id === branch['id']);
          if (searchFacet) {
            tree[index].children[0].value = facetValue.fieldValue;
          } else {
            if (tree[index].id === FILTER_PRICE_KEY || singleOptionFacet)
              tree[index] = { id: '', uniqueId: '', children: [] };
            tree[index] = mergeDeepFilters(tree[index], { ...branch });
          }
        } else {
          tree.push({ ...branch });
        }
      } else {
        tree.forEach((facet, i) => {
            FacetsService.findFacetAndDelete(
              tree[i].children,
              facetValue.uniqueId,
              false,
            );
            if (tree[i].children.length === 0) {
              tree.splice(i, 1);
            }
        });
      }

      this.facetService.addOrRemoveChipOnState(facetValue, selected);

      this.facetService.setFacetsState(tree as FilterRequestUnique[]);
      this.facetService.setFacetsRequestState(JSON.parse(JSON.stringify(tree)));
  }

  private renderFacetLabel() {
      return (
        this.facetTranslation ||
      this.labelProvider(this.facetTitle, this.facetTitle)
      );
  }

  private applyRtlForCheckboxes = (): void => {
      if (this.rtl) {
        const checkboxes = this.el.querySelectorAll('se-checkbox');
        checkboxes.forEach((el) => el.setAttribute('dir', 'rtl'));
      }
  };

  private searchableInputChanged(value: string): void {
      this.searchFacetValue = value;
      this.inputChangedEvent.emit({
        uniqueId: this.facetId,
        value: value,
      });
  }

  private dateFacetInputChanged(value: string, type: FacetType) {
      const facetData = this.facetData as FacetCose;
      this.facetChanged.emit({
        facetValue: value,
        facetType: type,
        facetName: value,
        facetCount: 0,
        facetQuery: 'date',
        action: 'add',
        id: facetData.id,
        searchFacet: false,
        value: value,
      });
  }

  private searchableInputOnKeyPress(e: KeyboardEvent, _value): void {
      if (e.key === 'Enter') {
        this.inputEnterClicked.emit();
      }
  }

  private handleScrollToTop = (): void => {
      if (!this.mobileView && this.scrollToTop) {
        this.scrollTopEvent.emit();
      }
  };

  private getShowMoreLabel = (): string => {
      if (this.hideFilterItems) {
        return `${this.commonFilterTranslations('filters.view-more', 'View more')}
      (${this.values.filter((x) => x.count > 0).length - this.itemsLimit}) `;
      } else {
        return this.commonFilterTranslations('filters.view-less', 'View less');
      }
  };

  render() {
      if (
        (!this.values || this.values.length === 0) &&
      (this.facetData as FacetCose)?.type !== FacetTypeEnum.Any
      ) {
        return <Host class="facet-box empty" data-facet={this.facetTitle} />;
      }
      if (
        (this.facetData as FacetCose)?.type === FacetTypeEnum.Any &&
      (this.facetData as FacetCose)?.expression != 'SINGLE'
      ) {
        return (
          <div class="facet-container">
            <div class="search-facet-input-container">
              <SearchableFacet
                inputChanged={(value) => this.searchableInputChanged(value)}
                onKeyPress={(event, value) =>
                  this.searchableInputOnKeyPress(event, value)
                }
                inputLabel={this.labelProvider(this.facetTitle, this.facetTitle)}
                placeholder={this.labelProvider(
                  'facet.placeholder.search',
                  'Search',
                )}
                inputValue={this.searchFacetValue}
              />
            </div>
          </div>
        );
      }

      if ((this.facetData as FacetCose)?.type === FacetTypeEnum.Date) {
        return (
          <div
            dir={this.rtl ? 'rtl' : 'auto'}
            class={`facet${addHorizontalClass(this.horizontalFilterPanel)}`}
          >
            {this.hideFacetTitle ? (
              <div data-info={'facet hidden title: ' + this.facetTitle} />
            ) : (
              this.renderFacetTitle()
            )}

            <div
              class={`facet__body facet__body${this.handleOpenView()}${addHorizontalClass(this.horizontalFilterPanel)}`}
            >
              <div
                class={`facet__accordion facet__accordion${this.handleOpenView()}`}
              >
                <date-filter-facet-component
                  labelProvider={this.labelProvider}
                  facets={(this.facetData as FacetCose)?.fieldValues}
                  validFacetValueChanged={(val, type) =>
                    this.dateFacetInputChanged(val, type)
                  }
                />
              </div>
            </div>
          </div>
        );
      }

      if (
        (this.facetData as FacetCose)?.type === FacetTypeEnum.Group &&
      (this.facetData as FacetCose)?.expression === 'SINGLE'
      ) {
        return (
          <div
            dir={this.rtl ? 'rtl' : 'auto'}
            class={`facet${addHorizontalClass(this.horizontalFilterPanel)}`}
          >
            {this.hideFacetTitle ? (
              <div data-info={'facet hidden title: ' + this.facetTitle} />
            ) : (
              this.renderFacetTitle()
            )}

            <div
              class={`facet__body-single-option facet__body-single-option${this.handleOpenView()}${addHorizontalClass(this.horizontalFilterPanel)}`}
            >
              <div
                class={`facet__accordion facet__accordion${this.handleOpenView()}`}
              >
                <se-list
                  option="filter"
                  selectedColor="primary"
                  class="single-option-filter-list"
                >
                  {this.renderSingleOptionFacetValues(
                    this.itemsLimit,
                    this.hideFilterItems,
                  )}
                </se-list>
                {this.itemsLimit &&
                this.itemsLimit <
                  this.values.filter((x) => x.count > 0).length && (
                  <div class="show-more-button-container">
                    <qds-button
                      size="small"
                      class="show-more-button--qds-button"
                      importance="subdued"
                      action={
                        this.hideFilterItems ? 'dropdown' : 'dropdown-close'
                      }
                      onClick={(e) => {
                          e.preventDefault();
                          this.handleClick();
                      }}
                      tabIndex={this.toggled ? 0 : -1}
                      text={this.getShowMoreLabel()}
                    ></qds-button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
      let rangeMarginValueStart;
      let rangeMarginValueEnd;
      if ((this.facetData as FacetCose)?.rangeType === FacetTypeEnum.RangePrice) {
        rangeMarginValueStart = +(this.facetData as any).fieldValues.find((val) => val.type === FacetTypeEnum.RangeStartMargin,).fieldValue; //eslint-disable-line @typescript-eslint/no-explicit-any
        rangeMarginValueEnd = +(this.facetData as any).fieldValues.find((val) => val.type === FacetTypeEnum.RangeEndMargin,).fieldValue; //eslint-disable-line @typescript-eslint/no-explicit-any
      }
      const lastSelectedIndex = this.values
        ?.filter((x) => x.count > 0)
        .map((el) => el.selected)
        .lastIndexOf(true);
      const isSelectedIdGreaterThanLimit = lastSelectedIndex
        ? this.itemsLimit > lastSelectedIndex
        : true;
      return (
        <div
          dir={this.rtl ? 'rtl' : 'auto'}
          class={`facet${addHorizontalClass(this.horizontalFilterPanel)}`}
        >
          {this.hideFacetTitle ? (
            <div data-info={'facet hidden title: ' + this.facetTitle} />
          ) : (
            this.renderFacetTitle()
          )}

          <div
            class={`facet__body facet__body${this.handleOpenView()}${addHorizontalClass(this.horizontalFilterPanel)}`}
          >
            <div
              class={`facet__accordion facet__accordion${this.handleOpenView()}`}
            >
              {(this.facetData as FacetCose)?.rangeType ===
            FacetTypeEnum.RangePrice ? (
                  <svs-range-slider
                    minValue={rangeMarginValueStart}
                    maxValue={rangeMarginValueEnd}
                  />
                ) : (
                  this.renderFacetValues(this.itemsLimit, this.hideFilterItems)
                )}
              {this.itemsLimit &&
              this.itemsLimit < this.values.filter((x) => x.count > 0).length &&
              isSelectedIdGreaterThanLimit && (
                <qds-button
                  class="show-more-button--qds-button"
                  size="small"
                  importance="subdued"
                  action={this.hideFilterItems ? 'dropdown' : 'dropdown-close'}
                  onClick={(e) => {
                      e.preventDefault();
                      this.handleClick();
                  }}
                  tabIndex={this.toggled ? 0 : -1}
                  text={this.getShowMoreLabel()}
                ></qds-button>
              )}
            </div>
          </div>
        </div>
      );
  }

  private updateSearchFacet(value: string): void {
      this.searchFacetValue = value;
      this.facetChanged.emit({
        facetValue: value,
        facetType: this.facetTitle,
        facetName: value,
        facetCount: 0,
        facetQuery: 'search',
        action: 'add',
        id: this.facetId,
        searchFacet: true,
        value: value,
      });
  }

  private facetTags(facetId: string) {
      return [
        <FacetTag
          isLabelVisible={facetId == FACET_TAG.CUSTOMIZATION}
          endDate='2025/01/31'
          label={this.labelProvider('additional-conditions.banner.new', 'New')} />
      ];
  }

  private renderFacetTitle() {
      if ((this.facetData as FacetCose)?.type === FacetTypeEnum.Any) {
        return (
          <label class="search_facet_title">
            {this.labelProvider(
              (this.facetData as FacetCose)?.displayName,
              (this.facetData as FacetCose)?.displayName,
            )}
          </label>
        );
      }

      return (
        <a
          class={'facet__header-link'}
          href=""
          onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                this.toggleComponent();
              }
          }}
        >
          <div
            class={`facet__header${addHorizontalClass(this.horizontalFilterPanel)}`}
            onClick={(e) => {
                e.preventDefault();
                this.toggleComponent();
                this.facetToggled.emit(this.index);
            }}
          >

            <div class={'ffn-header-label-wraper'}>
              {this.horizontalFilterPanel ? (
                <span class="facet__title">{this.renderFacetLabel()}</span>
              ) : (
                <h6 class="facet__title">{this.renderFacetLabel()}</h6>
              )}
              {this.facetTags(this.facetData?.id)}
            </div>

            <div>
              <div class={`facet__arrow${this.toggled ? '--rotated' : ''}`} />
            </div>
          </div>
        </a>
      );
  }

  private renderFacetValues(limit, hideState) {
      let className = '';
      if (!this.itemsLimit) {
        return this.values.map((v) => {
            if (v.count > 0) {
              return this.renderSingleFacetValue(v, className);
            }
        });
      } else {
      /*
        Checks is some filter has property selected===true and exist in hidden by limit partial
         */
        const someSelectedFilterIsInLimitPartial: boolean = this.values.some(
          (filter, index) => {
              return filter.selected && index + 1 > limit;
          },
        );
        if (
          (this.values && this.values.length <= limit) ||
        someSelectedFilterIsInLimitPartial
        ) {
          return this.values.map((v) => {
              if (v.count > 0) {
                return this.renderSingleFacetValue(v, className);
              }
          });
        } else if (this.values && this.values.length) {
          return this.values.map((v, index) => {
              if (index < limit) {
                if (v.count > 0) {
                  return this.renderSingleFacetValue(v, className);
                }
              } else {
                className = hideState ? 'facet__hide ' : '';
                if (v.count > 0) {
                  return this.renderSingleFacetValue(v, className);
                }
              }
          });
        }
      }
  }

  private renderSingleOptionFacetValues(limit, hideState) {
      if (!this.itemsLimit) {
        return this.values.map((v) => {
            if (v.count > 0) {
              return this.renderSingleOptionFacetValue(v);
            }
        });
      } else {
      /*
      Checks is some filter has property selected===true and exist in hidden by limit partial
       */
        const someSelectedFilterIsInLimitPartial: boolean = this.values.some(
          (filter, index) => {
              return filter.selected && index + 1 > limit;
          },
        );
        if (
          (this.values && this.values.length <= limit) ||
        someSelectedFilterIsInLimitPartial
        ) {
          return this.values.map((v) => {
              if (v.count > 0) {
                return this.renderSingleOptionFacetValue(v);
              }
          });
        } else if (this.values && this.values.length) {
          return this.values.map((v, index) => {
              if (index < limit) {
                if (v.count > 0) {
                  return this.renderSingleOptionFacetValue(v);
                }
              } else {
                if (v.count > 0 && !hideState) {
                  return this.renderSingleOptionFacetValue(v);
                }
              }
          });
        }
      }
  }
  private renderSingleOptionFacetValue(v: FacetValueCose | FacetValue) {
      return (
        <se-list-item key={v.fieldValue + v.uniqueId}
          item={
            (v.fieldTranslation ||
            v.displayName ||
            this.labelProvider(v.fieldValue, v.fieldValue)) +
          ' (' +
          v.count +
          ')'
          }
          value={v.fieldValue}
          selected={v.selected}
          disabled={
            this.disableFiltersOnLoading && this.reflectedState?.ajaxIsLoading
          }
          onClick={(e) => {
              this.toggled = true;
              e.preventDefault();
              e.stopPropagation();
              this.facetChanged.emit({
                facetValue: v.selected ? v.fieldValue : null,
                facetType: this.facetTitle,
                facetName: v.fieldValue,
                facetTranslation: v.fieldTranslation,
                facetTypeTranslation: this.facetTranslation,
                facetCount: v.count,
                facetQuery: 'single',
                value: v.fieldValue,
                action: 'add',
                id: this.facetId,
                uniqueId: v.uniqueId,
              });
              this.onFacetChange(v, true, false, null, true);
              this.handleScrollToTop();
          }}
          class="single-option-item-label"
        ></se-list-item>
      );
  }

  private onFacetSelect(v: FacetValue | FacetValueCose): boolean {
      return v.selected;
  }

  private renderSingleFacetValue(v: FacetValue, className) {
      return (
        <div key={v.fieldValue + v.uniqueId} class={'facet__checkbox ' + className}>
          <se-checkbox
            name={this.facetTitle}
            padding="small"
            label={
              (v.fieldTranslation ||
              v.displayName ||
              this.labelProvider(v.fieldValue, v.fieldValue)) +
            ' (' +
            v.count +
            ')'
            }
            value={v.fieldValue}
            //@ts-ignore DRUTY - wrong attribute or value
            query={v.query}
            selected={this.onFacetSelect(v)}
            disabled={
              this.disableFiltersOnLoading && this.reflectedState?.ajaxIsLoading
            }
            onDidChange={(e) => {
                this.toggled = true;
                e.preventDefault();
                e.stopPropagation();
                this.facetChanged.emit({
                  facetValue: e.detail.selected ? v.fieldValue : null,
                  facetType: this.facetTitle,
                  facetName: v.fieldValue,
                  facetTranslation: v.fieldTranslation,
                  facetTypeTranslation: this.facetTranslation,
                  facetCount: v.count,
                  facetQuery: v.query,
                  value: e?.detail?.value,
                  action: e.detail.selected ? 'add' : 'remove',
                  id: this.facetId,
                  uniqueId: v.uniqueId,
                });
                this.onFacetChange(v, e.detail.selected);
                this.handleScrollToTop();
            }}
            color={this.defaultQuartzColor}
            class="uiaas-search-facet__checkbox"
          />
        </div>
      );
  }

  disconnectedCallback(): void {
      this.unsubscriber$.complete();
  }
}
